import { Route, redirect, createBrowserRouter, createRoutesFromElements, RouterProvider } from 'react-router-dom';
import { Content } from './components/Content';
import { NewsItem } from './pages/NewsItem';
import { News } from './pages/News';
import { Home } from './pages/Home';
import { NotFound } from './pages/NotFound';
import { Download } from './pages/Download';
import { Changelog } from './pages/Changelog';
import { HostAuth } from './pages/HostAuth';
import ReactGA from 'react-ga4';
import { Privacy } from './pages/Privacy';
import { TermsOfService } from './pages/Terms';
import { FAQs } from './pages/FAQs';
import { AboutUs } from './pages/About';
import { Discord } from './pages/Discord';
import { ElectronAuth } from './pages/ElectronAuth';
import { useUser } from '@clerk/clerk-react';
import { useEffect } from 'react';
import * as Sentry from '@sentry/react';
import { TwitchbotCallback } from './pages/TwitchbotCallback';
import { Dev } from './pages/Dev';
import constants from '@playcastdotio/GlobalConfig';
import { getPersistentPlayerId } from '../metrics';
import { DiscordBot } from './pages/DiscordBot';
import { ObsQr } from './pages/ObsQr';
import { Affiliate } from './pages/Affiliate';

const trackingId = import.meta.env.VITE_GA_TRACKING_ID || constants.GA_TRACKING_ID;
if (trackingId) {
  const clerkUserId = localStorage.getItem('clerkUserID');
  let user_id = getPersistentPlayerId();
  if (clerkUserId && user_id !== clerkUserId && clerkUserId !== '') {
    user_id = clerkUserId;
  }

  const debug_mode = !(constants.VERSION.split('-')[1] === undefined);
  if (debug_mode) {
    ReactGA.initialize(trackingId, {
      gaOptions: {
        debug_mode,
      },
      gtagOptions: {
        debug_mode,
      },
    });
  } else {
    ReactGA.initialize(trackingId);
  }

  ReactGA.set({
    version: constants.VERSION,
    environment: constants.ENVIRONMENT,
    user_id,
  });

  if (typeof window !== 'undefined') {
    window.ReactGA = ReactGA;
  }
} else {
  console.warn('Google Analytics tracking ID not set');
}

const router = createBrowserRouter(
  createRoutesFromElements(
    <>
      <Route path='/' element={<Content />}>
        <Route index element={<Home />} />
        <Route path='/news'>
          <Route index element={<News />} />
          <Route path=':slug' element={<NewsItem />} />
        </Route>
        <Route path='/a'>
          <Route path=':affiliate' element={<Affiliate />} />
        </Route>
        <Route path='download' element={<Download />} />
        <Route path='host-auth' element={<HostAuth />} />
        <Route path='changelog' element={<Changelog />} />
        <Route path='privacy-policy' element={<Privacy />} />
        <Route path='terms-of-service' element={<TermsOfService />} />
        <Route path='discord' element={<Discord />} />
        <Route path='discord-bot' element={<DiscordBot />} />
        <Route path='faqs' element={<FAQs />} />
        <Route path='about-us' element={<AboutUs />} />
        <Route path='host-login' element={<ElectronAuth />} />
        <Route path='dev' element={<Dev />} />
        <Route path='twitchbot-callback' element={<TwitchbotCallback />} />
        {/* If we want to show the qr code with navbar uncomment below */}
        {/* <Route path='qrcode' element={<Qrcode />} /> */}
      </Route>
      <Route path='qr/:hash' element={<ObsQr />} />
      <Route
        path='/go'
        loader={async () => {
          return redirect('/j/ebzr80kddka14mr99k7hfs4g');
        }}
      />
      <Route path='*' element={<NotFound />} />
    </>,
  ),
);

export const App = () => {
  const user = useUser();
  useEffect(() => {
    if (user) {
      const username = user.user?.username ?? user.user?.fullName ?? undefined;
      const email = user.user?.primaryEmailAddress?.emailAddress;
      const id = user.user?.id;
      if (email && id) {
        if (Sentry.isInitialized()) {
          Sentry.setUser({
            username,
            email,
            id,
            ip_address: '{{auto}}',
          });
          console.log('Sentry.setUser');
        }
        ReactGA.set({ user_id: id });
        localStorage.setItem('clerkUserID', id ?? '');
      }
    }
  }, [user]);

  return <RouterProvider router={router} />;
};
